import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "photo-capture-page grid h-full w-full grid-rows-[auto,minmax(0,1fr),auto] justify-items-center overflow-hidden px-5 py-5 text-jm_white" }
const _hoisted_2 = { class: "photo-capture-header flex shrink-0 flex-col items-center" }
const _hoisted_3 = ["src"]
const _hoisted_4 = { class: "photo-capture-title mb-2 text-center font-bold uppercase leading-none" }
const _hoisted_5 = { class: "photo-capture-description max-w-[18rem] text-center leading-6" }
const _hoisted_6 = { class: "photo-capture-stage flex min-h-0 w-full max-w-[22rem] flex-col items-center justify-center py-3" }
const _hoisted_7 = { class: "photo-capture-preview relative flex w-full items-center justify-center overflow-hidden rounded-[2rem] border-2 border-jm_white/40 bg-black/30" }
const _hoisted_8 = ["src"]
const _hoisted_9 = {
  key: 2,
  class: "flex h-full w-full items-center justify-center px-8 text-center text-sm uppercase tracking-[0.2em] text-jm_white/70"
}
const _hoisted_10 = {
  key: 0,
  class: "mt-3 text-center text-sm leading-5 text-jm_white/80"
}
const _hoisted_11 = {
  key: 1,
  class: "mt-3 text-center text-sm leading-5 text-jm_orange"
}
const _hoisted_12 = { class: "photo-capture-actions flex w-full max-w-[22rem] shrink-0 flex-col gap-3 self-end" }
const _hoisted_13 = ["disabled"]
const _hoisted_14 = ["disabled"]
const _hoisted_15 = ["disabled"]
const _hoisted_16 = ["disabled"]

import { computed, nextTick, onBeforeUnmount, ref } from "vue";
import DefaultLayout from "../layouts/DefaultLayout.vue";
import logo from "@/assets/images/misc/logo_jaegermeister_white.svg";
import { useGlobalStore } from "@/stores/global";
import { useGameTracking } from "@/composables/useGameTracking";
import { uploadCapturedPhoto } from "@/services/pimImageUpload";

const normalizedPhotoMimeType = "image/jpeg";
const normalizedPhotoQuality = 0.86;
const maxPhotoDimension = 1440;


export default /*@__PURE__*/_defineComponent({
  __name: 'photoCapture',
  setup(__props) {

const globalStore = useGlobalStore();
const { kpiTracker, session } = useGameTracking();

const videoRef = ref<HTMLVideoElement | null>(null);
const fileInputRef = ref<HTMLInputElement | null>(null);
const mediaStream = ref<MediaStream | null>(null);
const isCameraOpen = ref(false);
const cameraError = ref("");
const uploadError = ref("");
const isUploadingPhoto = ref(false);
const shouldUseNativeCameraFallback = ref(false);
const capturedPhoto = computed(() => globalStore.capturedPhotoDataUrl);
const shouldPreferNativeCameraPicker = () => {
  const userAgent = navigator.userAgent || "";
  const isMobileUserAgent =
    /Android|iPhone|iPad|iPod|Mobile/i.test(userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  return isMobileUserAgent;
};

const stopCamera = () => {
  if (mediaStream.value) {
    mediaStream.value.getTracks().forEach((track) => track.stop());
    mediaStream.value = null;
  }

  isCameraOpen.value = false;
};

const openNativeCameraPicker = () => {
  cameraError.value = "";
  uploadError.value = "";
  shouldUseNativeCameraFallback.value = false;
  if (fileInputRef.value) {
    fileInputRef.value.value = "";
  }
  fileInputRef.value?.click();
};

const resetAfterPhotoStep = () => {
  globalStore.changeStep(
    globalStore.shouldShowDailyLimitScreen ? "thatsEnough" : "ageCheck"
  );
  globalStore.setShouldShowDailyLimitScreen(false);
  globalStore.setCapturedPhotoFile(null);
  globalStore.setCapturedPhotoDataUrl(null);
  globalStore.setUploadedPhotoItemId(null);
  globalStore.setRaffleResult(null);
  globalStore.playTapSound();
  kpiTracker.resetGame();
  kpiTracker.resetTrackingData();
  globalStore.resetGame(kpiTracker.selectedPerson.value);
};

const buildCameraErrorMessage = (reason: string) =>
  `Live camera access is unavailable (${reason}). Tap again to use the device camera instead.`;

const getCameraErrorReason = (error: unknown) => {
  if (!window.isSecureContext) {
    return "page is not running in a secure context";
  }

  if (!navigator.mediaDevices?.getUserMedia) {
    return "getUserMedia is not available in this browser";
  }

  if (error instanceof DOMException) {
    return `${error.name}: ${error.message}`;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "unknown browser error";
};

const isCameraPermissionDenied = (error: unknown) => {
  if (error instanceof DOMException) {
    return (
      error.name === "NotAllowedError" || error.name === "PermissionDeniedError"
    );
  }

  if (error instanceof Error) {
    return /denied|not allowed/i.test(error.message);
  }

  return false;
};

const loadImageFromFile = (file: File) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    const objectUrl = URL.createObjectURL(file);

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("The photo could not be loaded."));
    };
    image.src = objectUrl;
  });

const canvasToBlob = (canvas: HTMLCanvasElement) =>
  new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) {
          resolve(blob);
          return;
        }

        reject(new Error("The photo could not be created."));
      },
      normalizedPhotoMimeType,
      normalizedPhotoQuality
    );
  });

const getScaledPhotoSize = (width: number, height: number) => {
  const scale = Math.min(1, maxPhotoDimension / Math.max(width, height));

  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
};

const createNormalizedPhoto = async (
  source: HTMLImageElement | HTMLVideoElement,
  sourceWidth: number,
  sourceHeight: number
) => {
  const size = getScaledPhotoSize(sourceWidth, sourceHeight);
  const canvas = document.createElement("canvas");

  canvas.width = size.width;
  canvas.height = size.height;

  const context = canvas.getContext("2d");

  if (!context) {
    throw new Error("The photo could not be created.");
  }

  context.drawImage(source, 0, 0, size.width, size.height);

  const dataUrl = canvas.toDataURL(normalizedPhotoMimeType, normalizedPhotoQuality);
  const blob = await canvasToBlob(canvas);
  const file = new File([blob], `jaegermeister-shot-${Date.now()}.jpg`, {
    type: normalizedPhotoMimeType,
  });

  return { dataUrl, file };
};

const normalizeSelectedPhotoFile = async (file: File) => {
  const image = await loadImageFromFile(file);

  return createNormalizedPhoto(
    image,
    image.naturalWidth || image.width,
    image.naturalHeight || image.height
  );
};

const openFrontCamera = async () => {
  cameraError.value = "";
  uploadError.value = "";
  stopCamera();

  if (!window.isSecureContext || !navigator.mediaDevices?.getUserMedia) {
    throw new Error("live-camera-unavailable");
  }

  const cameraAttempts: MediaStreamConstraints[] = [
    {
      audio: false,
      video: {
        facingMode: {
          exact: "user",
        },
      },
    },
    {
      audio: false,
      video: {
        facingMode: {
          ideal: "user",
        },
      },
    },
    {
      audio: false,
      video: true,
    },
  ];

  let lastError: unknown = null;

  for (const constraints of cameraAttempts) {
    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      mediaStream.value = stream;
      isCameraOpen.value = true;
      await nextTick();

      if (videoRef.value) {
        videoRef.value.srcObject = stream;
        await videoRef.value.play();
      }

      return;
    } catch (error) {
      lastError = error;
      stopCamera();
    }
  }

  throw lastError ?? new Error("camera-access-failed");
};

const handleOpenCamera = async () => {
  if (shouldPreferNativeCameraPicker()) {
    openNativeCameraPicker();
    return;
  }

  if (cameraError.value || shouldUseNativeCameraFallback.value) {
    openNativeCameraPicker();
    return;
  }

  try {
    await openFrontCamera();
    shouldUseNativeCameraFallback.value = false;
  } catch (error) {
    if (isCameraPermissionDenied(error)) {
      cameraError.value = "";
      uploadError.value = "";
      shouldUseNativeCameraFallback.value = true;
      stopCamera();
      return;
    }

    cameraError.value = buildCameraErrorMessage(getCameraErrorReason(error));
    stopCamera();
    console.error("[PhotoCapture] Live camera access failed:", error, {
      isSecureContext: window.isSecureContext,
      hasMediaDevices: Boolean(navigator.mediaDevices),
      hasGetUserMedia: Boolean(navigator.mediaDevices?.getUserMedia),
      userAgent: window.navigator.userAgent,
    });
  }
};

const capturePhoto = async () => {
  if (!videoRef.value) {
    return;
  }

  const video = videoRef.value;
  const width = video.videoWidth || 720;
  const height = video.videoHeight || 1280;

  try {
    const normalizedPhoto = await createNormalizedPhoto(video, width, height);
    globalStore.setCapturedPhotoFile(normalizedPhoto.file);
    globalStore.setCapturedPhotoDataUrl(normalizedPhoto.dataUrl);
    globalStore.setUploadedPhotoItemId(null);
    uploadError.value = "";
    stopCamera();
  } catch (error) {
    console.error("[PhotoCapture] Error while creating photo:", error);
    cameraError.value = "The photo could not be created.";
  }
};

const handleFileSelection = async (event: Event) => {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];

  if (!file) {
    return;
  }

  cameraError.value = "";
  uploadError.value = "";
  shouldUseNativeCameraFallback.value = false;
  globalStore.setUploadedPhotoItemId(null);

  try {
    const normalizedPhoto = await normalizeSelectedPhotoFile(file);

    globalStore.setCapturedPhotoFile(normalizedPhoto.file);
    globalStore.setCapturedPhotoDataUrl(normalizedPhoto.dataUrl);
  } catch (error) {
    console.error("[PhotoCapture] Error while loading selected photo:", error);
    globalStore.setCapturedPhotoFile(null);
    globalStore.setCapturedPhotoDataUrl(null);
    uploadError.value =
      error instanceof Error ? error.message : "The photo could not be loaded.";
  } finally {
    input.value = "";
  }
};

const retakePhoto = async () => {
  globalStore.setCapturedPhotoFile(null);
  globalStore.setCapturedPhotoDataUrl(null);
  globalStore.setUploadedPhotoItemId(null);
  uploadError.value = "";
  shouldUseNativeCameraFallback.value = false;
  await handleOpenCamera();
};

const uploadPhotoAndTrackCrmData = async () => {
  if (!capturedPhoto.value) {
    throw new Error("Missing image data for PIM upload");
  }

  if (globalStore.uploadedPhotoItemId !== null) {
    return globalStore.uploadedPhotoItemId;
  }

  const itemId = await uploadCapturedPhoto(capturedPhoto.value);
  globalStore.setUploadedPhotoItemId(itemId);
  kpiTracker.setEnteredCrmData(true);

  await session.updateSessionProgress(kpiTracker.getKpiData(), {
    step: "photo_uploaded",
  });

  return itemId;
};

const handlePrimaryAction = async () => {
  if (isUploadingPhoto.value) {
    return;
  }

  if (!capturedPhoto.value) {
    resetAfterPhotoStep();
    return;
  }

  uploadError.value = "";
  globalStore.playTapSound();
  isUploadingPhoto.value = true;

  try {
    await uploadPhotoAndTrackCrmData();
    globalStore.changeStep("shareInstructions");
  } catch (error) {
    console.error("[PhotoCapture] Error while uploading photo:", error);
    uploadError.value =
      error instanceof Error ? error.message : "The photo could not be uploaded.";
  } finally {
    isUploadingPhoto.value = false;
  }
};

onBeforeUnmount(() => {
  stopCamera();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(DefaultLayout, { "background-image": "people" }, {
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("img", {
            src: _unref(logo),
            alt: "logo",
            class: "photo-capture-logo mb-4 w-[92px]"
          }, null, 8, _hoisted_3),
          _createElementVNode("div", _hoisted_4, _toDisplayString(_ctx.$t("photo_capture.title")), 1),
          _createElementVNode("div", _hoisted_5, _toDisplayString(_ctx.$t("photo_capture.description")), 1)
        ]),
        _createElementVNode("div", _hoisted_6, [
          _createElementVNode("div", _hoisted_7, [
            (isCameraOpen.value)
              ? (_openBlock(), _createElementBlock("video", {
                  key: 0,
                  ref_key: "videoRef",
                  ref: videoRef,
                  autoplay: "",
                  playsinline: "",
                  muted: "",
                  class: "h-full w-full object-cover"
                }, null, 512))
              : (capturedPhoto.value)
                ? (_openBlock(), _createElementBlock("img", {
                    key: 1,
                    src: capturedPhoto.value,
                    alt: "Captured selfie",
                    class: "h-full w-full object-cover"
                  }, null, 8, _hoisted_8))
                : (_openBlock(), _createElementBlock("div", _hoisted_9, _toDisplayString(_ctx.$t("photo_capture.placeholder")), 1))
          ]),
          (cameraError.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_10, _toDisplayString(cameraError.value), 1))
            : _createCommentVNode("", true),
          (uploadError.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_11, _toDisplayString(uploadError.value), 1))
            : _createCommentVNode("", true),
          _createElementVNode("input", {
            ref_key: "fileInputRef",
            ref: fileInputRef,
            type: "file",
            accept: "image/*",
            capture: "user",
            class: "pointer-events-none absolute left-[-9999px] top-0 h-px w-px opacity-0",
            onChange: handleFileSelection
          }, null, 544)
        ]),
        _createElementVNode("div", _hoisted_12, [
          (!isCameraOpen.value && !capturedPhoto.value)
            ? (_openBlock(), _createElementBlock("button", {
                key: 0,
                class: _normalizeClass(["photo-capture-button min-h-12 rounded-xl bg-jm_orange px-4 text-sm font-bold uppercase tracking-[0.15em] text-white", { 'cursor-not-allowed opacity-50': isUploadingPhoto.value }]),
                disabled: isUploadingPhoto.value,
                onClick: handleOpenCamera
              }, _toDisplayString(_ctx.$t("photo_capture.open_camera")), 11, _hoisted_13))
            : (isCameraOpen.value)
              ? (_openBlock(), _createElementBlock("button", {
                  key: 1,
                  class: _normalizeClass(["photo-capture-button min-h-12 rounded-xl bg-jm_orange px-4 text-sm font-bold uppercase tracking-[0.15em] text-white", { 'cursor-not-allowed opacity-50': isUploadingPhoto.value }]),
                  disabled: isUploadingPhoto.value,
                  onClick: capturePhoto
                }, _toDisplayString(_ctx.$t("photo_capture.capture_photo")), 11, _hoisted_14))
              : (_openBlock(), _createElementBlock("button", {
                  key: 2,
                  class: _normalizeClass(["photo-capture-button min-h-12 rounded-xl bg-jm_orange px-4 text-sm font-bold uppercase tracking-[0.15em] text-white", { 'cursor-not-allowed opacity-50': isUploadingPhoto.value }]),
                  disabled: isUploadingPhoto.value,
                  onClick: retakePhoto
                }, _toDisplayString(_ctx.$t("photo_capture.retake_photo")), 11, _hoisted_15)),
          _createElementVNode("button", {
            class: _normalizeClass(["photo-capture-button min-h-12 rounded-xl border-2 border-jm_white/70 bg-white/10 px-4 text-sm font-bold uppercase tracking-[0.15em] text-jm_white backdrop-blur-sm", { 'cursor-not-allowed opacity-50': isUploadingPhoto.value }]),
            disabled: isUploadingPhoto.value,
            onClick: handlePrimaryAction
          }, _toDisplayString(capturedPhoto.value
              ? _ctx.$t("photo_capture.continue")
              : _ctx.$t("photo_capture.skip")), 11, _hoisted_16)
        ])
      ])
    ]),
    _: 1
  }))
}
}

})